import { parseMessageId } from '../messageId';
import { parseXml, xmlSelector, type XmlNode } from '../xml';
import type { ValidationIssue, ValidationResult } from '../../types';
import { childDecls, resolveType } from './model';
import type { SchemaElementDecl, SchemaFacets, SchemaModel, SchemaTypeDef } from './types';

export type ValidateXmlOptions = {
  /** Override message short/id instead of deriving from xmlns */
  messageId?: string;
  models: Record<string, SchemaModel>;
};

/**
 * Validate an ISO 20022 Document against the compact schema model compiled
 * from the vendored XSD (required children, occurs, choice, facets, attrs).
 * Also runs NbOfTxs / CtrlSum heuristics used by schemes.
 */
export function validateXmlAgainstSchema(xml: string, options: ValidateXmlOptions): ValidationResult {
  const { models } = options;
  const parsed = parseXml(xml);
  const errors: ValidationIssue[] = [];
  const warnings: ValidationIssue[] = [];

  if (!parsed.wellFormed || !parsed.root) {
    return {
      wellFormed: false,
      messageShort: null,
      detectedNamespace: null,
      errors: [
        {
          path: '/',
          rule: 'well-formedness',
          message: parsed.error ?? 'Document is not well-formed XML.',
        },
      ],
      warnings: [],
      stats: parsed.stats,
    };
  }

  const declaredId = options.messageId ?? parsed.messageId ?? null;
  const parts = declaredId ? parseMessageId(declaredId) : null;
  const short = parts?.valid ? parts.short : null;
  const model = short ? models[short] : undefined;

  if (!parsed.namespace) {
    warnings.push({
      path: 'Document',
      rule: 'namespace',
      message: 'No xmlns on the root element. Most schemes reject documents without a declared namespace.',
    });
  }

  if (parsed.root.name !== 'Document') {
    errors.push({
      path: parsed.root.name,
      rule: 'root-element',
      message: `Root element is <${parsed.root.name}>. ISO 20022 documents are wrapped in <Document>.`,
    });
  }

  if (!model) {
    warnings.push({
      path: 'Document@xmlns',
      rule: 'message-type',
      message: declaredId
        ? `${declaredId} has no compiled XSD model in this explorer, so only well-formedness and count checks ran.`
        : 'Could not derive a message type from the namespace, so only well-formedness and count checks ran.',
    });
  } else {
    if (
      parsed.namespace &&
      model.targetNamespace &&
      parsed.namespace !== model.targetNamespace
    ) {
      warnings.push({
        path: 'Document@xmlns',
        rule: 'namespace-revision',
        message: `xmlns is ${parsed.namespace}; teaching XSD model is ${model.messageId} (${model.targetNamespace}). Structure rules still apply.`,
      });
    }

    const docChild = parsed.root.children[0];
    if (!docChild) {
      errors.push({
        path: 'Document',
        rule: 'mandatory-element',
        message: `Missing mandatory element: ${model.rootElement}`,
      });
    } else if (docChild.name !== model.rootElement) {
      errors.push({
        path: xmlSelector(docChild),
        rule: 'message-root',
        message: `Expected <${model.rootElement}> for ${model.short} but found <${docChild.name}>. The namespace and the payload disagree.`,
      });
    } else {
      validateNode(model, docChild, model.documentChild.typeName, errors);
    }
  }

  checkCounts(xml, warnings, errors);

  return {
    wellFormed: true,
    messageShort: short,
    detectedNamespace: parsed.namespace,
    errors,
    warnings,
    stats: parsed.stats,
  };
}

function validateNode(
  model: SchemaModel,
  node: XmlNode,
  typeName: string,
  errors: ValidationIssue[],
): void {
  const type = resolveType(model, typeName);
  if (!type) return;

  validateAttributes(model, node, type, errors);

  if (type.kind === 'simple' || (type.kind === 'simpleContent' && node.children.length === 0)) {
    validateFacets(node, facetsFor(model, type), errors);
    return;
  }

  if (type.kind === 'choice' || type.choice?.length) {
    validateChoice(model, node, type.choice ?? type.children ?? [], errors);
    return;
  }

  const decls = childDecls(model, typeName);
  if (decls.length === 0) {
    if (node.children.length === 0) {
      validateFacets(node, facetsFor(model, type), errors);
    }
    return;
  }

  validateSequence(model, node, decls, errors);
}

function validateSequence(
  model: SchemaModel,
  node: XmlNode,
  decls: SchemaElementDecl[],
  errors: ValidationIssue[],
): void {
  const byName = new Map<string, SchemaElementDecl>();
  for (const d of decls) byName.set(d.name, d);

  const counts = new Map<string, number>();
  for (const child of node.children) {
    counts.set(child.name, (counts.get(child.name) ?? 0) + 1);
  }

  for (const decl of decls) {
    const n = counts.get(decl.name) ?? 0;
    if (n < decl.minOccurs) {
      errors.push({
        path: xmlSelector(node),
        rule: 'mandatory-element',
        message:
          decl.minOccurs === 1 && n === 0
            ? `Missing mandatory element: ${decl.name}`
            : `<${decl.name}> occurs ${n} time(s); schema requires at least ${decl.minOccurs}.`,
      });
    }
    if (decl.maxOccurs !== 'unbounded' && n > decl.maxOccurs) {
      errors.push({
        path: xmlSelector(node),
        rule: 'max-occurs',
        message: `<${decl.name}> occurs ${n} time(s); schema allows at most ${decl.maxOccurs}.`,
      });
    }
  }

  for (const child of node.children) {
    const decl = byName.get(child.name);
    if (!decl) {
      errors.push({
        path: xmlSelector(child),
        rule: 'unexpected-element',
        message: `<${child.name}> is not allowed under <${node.name}> in this schema.`,
      });
      continue;
    }
    validateNode(model, child, decl.typeName, errors);
  }
}

function validateChoice(
  model: SchemaModel,
  node: XmlNode,
  decls: SchemaElementDecl[],
  errors: ValidationIssue[],
): void {
  const allowed = new Set(decls.map((d) => d.name));
  const present = node.children.filter((c) => allowed.has(c.name));
  const unexpected = node.children.filter((c) => !allowed.has(c.name));

  for (const child of unexpected) {
    errors.push({
      path: xmlSelector(child),
      rule: 'unexpected-element',
      message: `<${child.name}> is not allowed in this choice under <${node.name}>.`,
    });
  }

  if (present.length === 0) {
    const names = decls.map((d) => d.name).join(' | ');
    errors.push({
      path: xmlSelector(node),
      rule: 'choice',
      message: `Choice under <${node.name}> requires one of: ${names}.`,
    });
    return;
  }

  if (present.length > 1) {
    errors.push({
      path: xmlSelector(node),
      rule: 'choice',
      message: `Choice under <${node.name}> allows one branch; found ${present.map((c) => c.name).join(', ')}.`,
    });
  }

  for (const child of present) {
    const decl = decls.find((d) => d.name === child.name)!;
    validateNode(model, child, decl.typeName, errors);
  }
}

function validateAttributes(
  model: SchemaModel,
  node: XmlNode,
  type: SchemaTypeDef,
  errors: ValidationIssue[],
): void {
  const attrs = type.attributes ?? [];
  for (const attr of attrs) {
    const value = node.attributes[attr.name];
    if (attr.required && (value == null || value === '')) {
      errors.push({
        path: xmlSelector(node),
        rule: 'mandatory-attribute',
        message: `Missing mandatory attribute @${attr.name} on <${node.name}>.`,
      });
      continue;
    }
    if (value == null) continue;
    const attrType = resolveType(model, attr.typeName);
    const facets = facetsFor(model, attrType);
    const issue = facetIssue(value, facets);
    if (issue) {
      errors.push({
        path: xmlSelector(node, attr.name),
        rule: 'facet',
        message: `@${attr.name} on <${node.name}>: ${issue}`,
      });
    }
  }
}

function validateFacets(node: XmlNode, facets: SchemaFacets | undefined, errors: ValidationIssue[]): void {
  if (!facets) return;
  const value = node.text ?? '';
  const issue = facetIssue(value, facets);
  if (issue) {
    errors.push({
      path: xmlSelector(node),
      rule: 'facet',
      message: `<${node.name}>: ${issue}`,
    });
  }
}

function facetsFor(model: SchemaModel, type: SchemaTypeDef | undefined): SchemaFacets | undefined {
  if (!type) return undefined;
  if (type.facets) return type.facets;
  if (type.contentType) {
    const inner = resolveType(model, type.contentType);
    return inner?.facets;
  }
  return undefined;
}

function facetIssue(value: string, facets: SchemaFacets | undefined): string | null {
  if (!facets) return null;

  if (facets.enumeration?.length && !facets.enumeration.includes(value)) {
    const shown =
      facets.enumeration.length <= 8
        ? facets.enumeration.join(' | ')
        : `${facets.enumeration.slice(0, 6).join(' | ')}…`;
    return `value "${value}" is not in enumeration (${shown})`;
  }

  if (facets.pattern) {
    try {
      if (!new RegExp(`^(?:${facets.pattern})$`).test(value)) {
        return `value "${value}" does not match pattern ${facets.pattern}`;
      }
    } catch {
      /* ignore invalid patterns from schema extract */
    }
  }

  if (facets.length != null && value.length !== facets.length) {
    return `length ${value.length} ≠ required length ${facets.length}`;
  }
  if (facets.minLength != null && value.length < facets.minLength) {
    return `length ${value.length} < minLength ${facets.minLength}`;
  }
  if (facets.maxLength != null && value.length > facets.maxLength) {
    return `length ${value.length} > maxLength ${facets.maxLength}`;
  }

  if (facets.base === 'decimal' || facets.base === 'integer' || facets.fractionDigits != null) {
    if (value !== '' && !/^-?\d+(\.\d+)?$/.test(value)) {
      return `value "${value}" is not a valid decimal`;
    }
  }

  if (facets.base === 'date' && value !== '' && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `value "${value}" is not a valid date (YYYY-MM-DD)`;
  }

  if (
    facets.base === 'dateTime' &&
    value !== '' &&
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)
  ) {
    return `value "${value}" is not a valid dateTime`;
  }

  if (facets.base === 'boolean' && value !== '' && !/^(true|false|0|1)$/.test(value)) {
    return `value "${value}" is not a valid boolean`;
  }

  if (facets.minInclusive != null && value !== '' && Number(value) < Number(facets.minInclusive)) {
    return `value ${value} < minInclusive ${facets.minInclusive}`;
  }
  if (facets.maxInclusive != null && value !== '' && Number(value) > Number(facets.maxInclusive)) {
    return `value ${value} > maxInclusive ${facets.maxInclusive}`;
  }

  return null;
}

function checkCounts(xml: string, warnings: ValidationIssue[], errors: ValidationIssue[]) {
  const declared = /<NbOfTxs>(\d+)<\/NbOfTxs>/.exec(xml);
  if (!declared) return;

  const txCount =
    countTag(xml, 'CdtTrfTxInf') ||
    countTag(xml, 'DrctDbtTxInf') ||
    countTag(xml, 'TxInf') ||
    countTag(xml, 'TxInfAndSts');

  if (txCount === 0) {
    warnings.push({
      path: 'GrpHdr/NbOfTxs',
      rule: 'transaction-count',
      message: 'NbOfTxs is declared but no transaction blocks were recognised.',
    });
    return;
  }

  const declaredCount = Number(declared[1]);
  if (declaredCount !== txCount) {
    errors.push({
      path: 'GrpHdr/NbOfTxs',
      rule: 'transaction-count',
      message: `NbOfTxs says ${declaredCount} but ${txCount} transaction blocks are present. Schemes reject this as AM18.`,
    });
  }

  const ctrlSum = /<CtrlSum>([\d.]+)<\/CtrlSum>/.exec(xml);
  const amounts = [...xml.matchAll(/<(?:InstdAmt|IntrBkSttlmAmt)[^>]*>([\d.]+)</g)].map((m) =>
    Number(m[1]),
  );
  if (ctrlSum && amounts.length > 0) {
    const total = amounts.reduce((a, b) => a + b, 0);
    const declaredSum = Number(ctrlSum[1]);
    if (Math.abs(total - declaredSum) > 0.005) {
      errors.push({
        path: 'GrpHdr/CtrlSum',
        rule: 'control-sum',
        message: `CtrlSum is ${declaredSum.toFixed(2)} but the amounts total ${total.toFixed(2)}.`,
      });
    }
  }
}

function countTag(xml: string, tag: string): number {
  return (xml.match(new RegExp(`<${tag}[\\s>]`, 'g')) ?? []).length;
}
